import { motion } from 'framer-motion';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { KimThreadStatus } from '../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetKimThreads } from '@/api/queries/useGetKimThreads';
import { useGetMatterKimThreads } from '@/api/queries/useGetMatterKimThreads';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface RecentKimThreadsProps {
  matterId: string;
  subsetId?: string;
  isSubsetMode?: boolean;
}

interface RecentThread {
  id: string;
  name: string;
  createdDate: Date | string;
  createdById: string;
  status: KimThreadStatus;
  subset: { id: string; name: string } | null;
}

export function RecentKimThreads({ matterId, subsetId, isSubsetMode = false }: RecentKimThreadsProps) {
  const { user } = useUserContext();

  const { data: subsetThreads, isLoading: isLoadingSubset } = useGetKimThreads(matterId, subsetId, isSubsetMode);
  const { data: matterThreads, isLoading: isLoadingMatter } = useGetMatterKimThreads(matterId, subsetId, !isSubsetMode);

  const threads: RecentThread[] | undefined = isSubsetMode
    ? subsetThreads?.map((thread) => ({ ...thread, subset: null }))
    : matterThreads;
  const isLoading = isSubsetMode ? isLoadingSubset : isLoadingMatter;

  if (isLoading || !threads) return null;

  const recentThreads = threads
    .filter((thread) => thread.createdById === user?.id && thread.status !== KimThreadStatus.Failed)
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    .slice(0, 4);

  if (recentThreads.length === 0) return null;

  return (
    <motion.div
      className="overflow-hidden"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      transition={{
        height: { duration: 0.2 },
        opacity: { duration: 0.15, delay: 0.1 },
      }}
    >
      <p className="mb-1.5 ml-2 text-xs text-gray-400">Pick up where you left off</p>
      <div className="w-full max-w-sm rounded-xl border bg-white p-1">
        <div className="flex flex-col">
          {recentThreads.map((thread) => (
            <Link
              key={thread.id}
              to={`/matters/${matterId}/subsets/${thread.subset?.id ?? subsetId}/new-kim/${thread.id}`}
              onClick={() =>
                trackCustomEvent(TrackingEventsV2.KimOpenRecentThread, {
                  matterId,
                  subsetId: thread.subset?.id ?? subsetId,
                  threadId: thread.id,
                })
              }
              className="group flex items-center justify-between gap-2 rounded-lg p-1 px-2 text-[13px] text-gray-600 hover:bg-gray-50"
            >
              <p className="flex min-w-0 flex-1 items-center gap-1.5">
                <Tooltip>
                  <TooltipTrigger className="mr-0.5 flex shrink-0 items-center">
                    <Icon className="lucide--clock text-gray-300" />
                  </TooltipTrigger>
                  <TooltipContent sideOffset={17} side="left">
                    {moment(thread.createdDate).fromNow()}
                  </TooltipContent>
                </Tooltip>

                {thread.subset && (
                  <Tooltip>
                    <TooltipTrigger className="flex max-w-20 shrink-0 items-center gap-1 rounded-md bg-sky-50 p-0.75 py-0.5 pl-1">
                      <Icon className="lucide--group text-sky-500" />
                      <span className="truncate text-[11px] text-sky-500">{thread.subset.name}</span>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={6}>{thread.subset.name}</TooltipContent>
                  </Tooltip>
                )}

                <span className="truncate">{thread.name}</span>
              </p>

              <Icon className="lucide--chevron-right text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-500" />
            </Link>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
